import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { EPlaybackRateMode } from '@bufferController/PlaybackSpeedController/IPlaybackSpeedController';
import { PlaybackSpeedEvents, TargetBufferEvents } from '@bufferController/PlaybackSpeedController/TargetBuffer.events';
import { ITargetBufferDecreaserConfig } from '@bufferController/PlaybackSpeedController/TargetBufferDecreaser/TargetBufferDecreaser.config';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { EventsInPeriodWindow } from '@utils/common/eventsInPeriodWindow';
import bind from '@utils/decorators/bind';

export class TargetBufferDecreaser extends ConfigConsumer<ITargetBufferDecreaserConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['PlaybackSpeed'];
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public currentTargetBufferMultiplier: number = 1;

  private _nextCheckTimestamp: number = 0;
  private _currentPlaybackRateMode: EPlaybackRateMode = EPlaybackRateMode.NORMAL;

  private readonly _slowdownEventsCollector: EventsInPeriodWindow = new EventsInPeriodWindow();
  private readonly _onPlaybackRateModeChangedListener: string;
  private readonly _onBufferMetricsAvailableListener: string;
  private readonly _onTargetBufferIncreasedListener: string;

  constructor(eventBus: TEventBus) {
    super('targetBufferDecreaser', eventBus);

    this._onPlaybackRateModeChangedListener = this._eventBus.subscribe(
      PlaybackSpeedEvents.setPlaybackSpeed,
      this._onPlaybackRateModeChanged,
    );
    this._onBufferMetricsAvailableListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsAvailable,
    );
    this._onTargetBufferIncreasedListener = this._eventBus.subscribe(
      TargetBufferEvents.targetBufferIncreased,
      this._onTargetBufferIncreased,
    );
  }

  @bind
  public destroy(): void {
    super.destroy();
    this._eventBus.unsubscribe(this._onPlaybackRateModeChangedListener);
    this._eventBus.unsubscribe(this._onBufferMetricsAvailableListener);
    this._eventBus.unsubscribe(this._onTargetBufferIncreasedListener);
  }

  @bind
  public _handleConfigUpdated(): void {}

  @bind
  private _onPlaybackRateModeChanged(rateMode: EPlaybackRateMode): void {
    if (
      rateMode !== this._currentPlaybackRateMode &&
      (rateMode === EPlaybackRateMode.SLOW || this._currentPlaybackRateMode === EPlaybackRateMode.SLOW)
    ) {
      this._slowdownEventsCollector.registerEvent();
    }

    this._currentPlaybackRateMode = rateMode;
  }

  @bind
  private _onBufferMetricsAvailable(): void {
    if (this._nextCheckTimestamp !== 0 && this._nextCheckTimestamp < Date.now()) {
      if (
        this.currentTargetBufferMultiplier > 1 &&
        this._currentPlaybackRateMode !== EPlaybackRateMode.SLOW &&
        this._slowdownEventsCollector.checkEventsInPeriod(this.config.periodOfEventsToCancelDecrease) === 0
      ) {
        this._eventBus.publish(TargetBufferEvents.targetBufferDecreaseRequested, {});
        this.currentTargetBufferMultiplier -= 1;
        this._nextCheckTimestamp = this.currentTargetBufferMultiplier > 1 ? Date.now() + this.config.periodOfEventsToCancelDecrease : 0;
      }
    }
  }

  @bind
  private _onTargetBufferIncreased(): void {
    this.currentTargetBufferMultiplier += 1;
    this._nextCheckTimestamp = Date.now() + this.config.periodOfEventsToCancelDecrease;
  }
}
